import * as THREE from 'three';
import { Panel } from '../ui/panel';
import { t } from '../i18n';
import { acquireMenuPointer, releaseMenuPointer } from '../ui/pointer';
import { placePanelInFront } from '../ui/placement';
import { contentBounds, onAssetsReady, paintPageTitleRow, treatiseBodyFont } from '../ui/style';
import type { PanelButton } from '../ui/button';
import {
  ARCADE_CUT_DIR_TOLERANCE_MAX_DEG,
  ARCADE_CUT_DIR_TOLERANCE_MIN_DEG,
  ARCADE_CUT_DIR_TOLERANCE_STEP_DEG,
  ARCADE_MIN_SPEED_MAX_MPS,
  ARCADE_MIN_SPEED_MIN_MPS,
  ARCADE_MIN_SPEED_STEP_MPS,
  ARCADE_SIZE_FACTOR_MAX,
  ARCADE_SIZE_FACTOR_MIN,
  ARCADE_SIZE_FACTOR_STEP,
  ARCADE_SPAWN_DELAY_MAX_MS,
  ARCADE_SPAWN_DELAY_MIN_MS,
  ARCADE_SPAWN_DELAY_STEP_MS,
  ARCADE_TTL_MAX_MS,
  ARCADE_TTL_MIN_MS,
  ARCADE_TTL_STEP_MS,
  AUDIO_VOLUME_MAX,
  AUDIO_VOLUME_MIN,
  AUDIO_VOLUME_STEP,
  DOMINANT_HAND,
  GUARD_TUNING_RANGES,
  PELL_STRIKE_MIN_ARC_MAX_M,
  PELL_STRIKE_MIN_ARC_MIN_M,
  PELL_STRIKE_MIN_ARC_STEP_M,
  PELL_STRIKE_MIN_SPEED_MAX_MPS,
  PELL_STRIKE_MIN_SPEED_MIN_MPS,
  PELL_STRIKE_MIN_SPEED_STEP_MPS,
  PELL_ZONE_BAND_MIN_GAP,
  PELL_ZONE_BAND_STEP,
  SETTINGS_ANGLE_MAX_DEG,
  SETTINGS_ANGLE_MIN_DEG,
  SETTINGS_ANGLE_STEP_DEG,
  SETTINGS_HEIGHT_MAX_CM,
  SETTINGS_HEIGHT_MIN_CM,
  SETTINGS_HEIGHT_STEP_CM,
  SETTINGS_PANEL_HEIGHT_M,
  SETTINGS_PANEL_WIDTH_M,
  TARGETS_DISTANCE_MAX_M,
  TARGETS_DISTANCE_MIN_M,
  TARGETS_DISTANCE_STEP_M,
  TARGETS_MIN_SPEED_MAX_MPS,
  TARGETS_MIN_SPEED_MIN_MPS,
  TARGETS_MIN_SPEED_STEP_MPS,
  TARGETS_SIZE_FACTOR_MAX,
  TARGETS_SIZE_FACTOR_MIN,
  TARGETS_SIZE_FACTOR_STEP,
  TARGETS_SPAWN_DELAY_MAX_MS,
  TARGETS_SPAWN_DELAY_MIN_MS,
  TARGETS_SPAWN_DELAY_STEP_MS,
  TARGETS_TIP_MIN_T_MAX,
  TARGETS_TIP_MIN_T_MIN,
  TARGETS_TIP_MIN_T_STEP,
  TARGETS_TTL_MAX_MS,
  TARGETS_TTL_MIN_MS,
  TARGETS_TTL_STEP_MS,
  TURN_MODE,
  UI_INK,
  UI_INK_MUTED,
  type DominantHand,
  type GuardTuning,
  type TurnMode,
} from '../config/constants';

export type SettingsMode = 'pell' | 'targets' | 'guard' | 'arcade';

export type SettingsState = {
  heightCm: number;
  pitchDeg: number;
  yawDeg: number;
  rollDeg: number;
  dominantHand: DominantHand;
  turnMode: TurnMode;
  /** Active drill — selects the content of the mode-specific tab. */
  mode: SettingsMode;
  /**
   * Arcade preset run (#43): the mode tunables are owned by the difficulty bundle, so the Modalità
   * tab shows them read-only (values, no ± buttons). False/absent = editable (custom or other drills).
   */
  modeReadOnly?: boolean;
  /** Pell mode: the 4 internal zone boundaries (fractions of height), bottom→top. */
  zoneBoundaries?: number[];
  /** Pell mode: minimum impact tip speed (m/s) for a valid colpo. */
  pellMinSpeed?: number;
  /** Pell mode: minimum swing-arc length (m) for a valid colpo. */
  pellMinArc?: number;
  /** Targets mode: disc size multiplier. */
  targetsSizeFactor?: number;
  /** Targets mode: minimum tip speed (m/s). */
  targetsMinSpeed?: number;
  /** Targets mode: spawn distance (m). */
  targetsDistanceM?: number;
  /** Targets mode: target lifetime before auto-miss (ms). */
  targetsTtlMs?: number;
  /** Targets mode: delay between targets (ms). */
  targetsSpawnDelayMs?: number;
  /** Thrust (custom): tip gate — fraction of the blade near the punta that counts as a thrust. */
  targetsTipMinT?: number;
  /** Arcade mode: target lifetime before auto-miss (ms). */
  arcadeTtlMs?: number;
  /** Arcade mode: delay between targets (ms). */
  arcadeSpawnDelayMs?: number;
  /** Arcade mode: angular tolerance (deg) on the required cut direction. */
  arcadeCutToleranceDeg?: number;
  /** Arcade mode: disc size multiplier. */
  arcadeSizeFactor?: number;
  /** Arcade cut (custom): minimum tip speed (m/s) for a slice to count. */
  arcadeMinSpeed?: number;
  /** Guard mode: the live-tunable recognition parameters. */
  guardTuning?: GuardTuning;
  /** Master SFX volume (0..1) — Audio tab. */
  sfxVolume: number;
  /** Background-music volume (0..1) — Audio tab. */
  musicVolume: number;
};

export type SettingsHandlers = {
  onChange: (next: SettingsState) => void;
  onClose: () => void;
  onRestart: () => void;
  onCancelSession: () => void;
};

type TabId = 'general' | 'mode' | 'audio';

/** A single ±-adjustable row in a tab. */
type StepRow = { label: string; onMinus: () => void; onPlus: () => void };

/**
 * In-session settings panel. Content is split into tabs (like the summary panel): "Generali"
 * (height, sword orientation, hand, turn) and "Modalità" (pell zone bands, or targets
 * size/speed/distance). The action row (extras + Close) stays pinned at the bottom.
 */
export class SettingsPanel {
  private panel: Panel | null = null;
  private state: SettingsState | null = null;
  private handlers: SettingsHandlers | null = null;
  private unsubscribe: (() => void) | null = null;
  private activeTab: TabId = 'general';

  constructor(
    private scene: THREE.Scene,
    private camera: THREE.Camera,
  ) {}

  isVisible(): boolean {
    return this.panel !== null;
  }

  show(initial: SettingsState, handlers: SettingsHandlers): void {
    if (this.panel) return;
    this.state = { ...initial };
    this.handlers = handlers;
    this.activeTab = 'general';

    const panel = new Panel({ widthM: SETTINGS_PANEL_WIDTH_M, heightM: SETTINGS_PANEL_HEIGHT_M });
    this.panel = panel;
    acquireMenuPointer();
    placePanelInFront(panel.mesh, this.camera);
    this.scene.add(panel.mesh);

    this.unsubscribe = onAssetsReady(() => this.repaint());
    this.repaint();
  }

  hide(): void {
    if (!this.panel) return;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.scene.remove(this.panel.mesh);
    this.panel.dispose();
    this.panel = null;
    releaseMenuPointer();
    this.state = null;
    this.handlers = null;
  }

  private repaint(): void {
    if (!this.panel || !this.state) return;
    const panel = this.panel;
    const cw = panel.canvas.width;
    const ch = panel.canvas.height;
    const b = contentBounds(cw, ch);

    const buttons: PanelButton[] = [];
    const labels: Array<{ text: string; x: number; y: number; muted?: boolean }> = [];

    // ── Tab row ──────────────────────────────────────────────
    const tabs: Array<{ id: TabId; label: string }> = [
      { id: 'general', label: t('settingsTabGeneral') },
      { id: 'mode', label: t('settingsTabMode') },
      { id: 'audio', label: t('settingsTabAudio') },
    ];
    const tabGap = cw * 0.012;
    const tabW = (b.w - tabGap * (tabs.length - 1)) / tabs.length;
    const tabY = ch * 0.155;
    const tabH = ch * 0.07;
    tabs.forEach((tab, i) => {
      buttons.push({
        rect: { x: b.x + i * (tabW + tabGap), y: tabY, w: tabW, h: tabH },
        label: tab.label,
        variant: tab.id === this.activeTab ? 'primary' : 'secondary',
        onClick: () => {
          this.activeTab = tab.id;
          this.repaint();
        },
      });
    });

    // ── Tab content ──────────────────────────────────────────
    if (this.activeTab === 'general') {
      this.layoutGeneral(buttons, labels, cw, ch);
    } else if (this.activeTab === 'audio') {
      this.layoutAudio(buttons, labels, cw, ch);
    } else {
      this.layoutMode(buttons, labels, cw, ch);
    }

    // ── Pinned action row (extras + Close) ───────────────────
    this.layoutActions(buttons, cw, ch);

    panel.setButtons(buttons);
    panel.renderFrame();
    const c = panel.getContext();
    paintPageTitleRow(c, t('settingsTitle'), cw, ch);

    c.save();
    c.textAlign = 'left';
    c.textBaseline = 'middle';
    c.font = treatiseBodyFont(ch * 0.045, 400);
    for (const lbl of labels) {
      c.fillStyle = lbl.muted ? UI_INK_MUTED : UI_INK;
      c.fillText(lbl.text, lbl.x, lbl.y);
    }
    c.restore();

    panel.paintButtons(c);
    panel.markDirty();
  }

  // ── General tab: height, pitch, yaw, roll, hand, turn ──────
  private layoutGeneral(
    buttons: PanelButton[],
    labels: Array<{ text: string; x: number; y: number; muted?: boolean }>,
    cw: number,
    ch: number,
  ): void {
    const s = this.state!;
    const rows: StepRow[] = [
      {
        label: t('settingsHeight')(s.heightCm),
        onMinus: () =>
          this.mutate({
            heightCm: clamp(
              s.heightCm - SETTINGS_HEIGHT_STEP_CM,
              SETTINGS_HEIGHT_MIN_CM,
              SETTINGS_HEIGHT_MAX_CM,
            ),
          }),
        onPlus: () =>
          this.mutate({
            heightCm: clamp(
              s.heightCm + SETTINGS_HEIGHT_STEP_CM,
              SETTINGS_HEIGHT_MIN_CM,
              SETTINGS_HEIGHT_MAX_CM,
            ),
          }),
      },
      {
        label: t('settingsPitch')(s.pitchDeg),
        onMinus: () =>
          this.mutate({
            pitchDeg: clamp(
              s.pitchDeg - SETTINGS_ANGLE_STEP_DEG,
              SETTINGS_ANGLE_MIN_DEG,
              SETTINGS_ANGLE_MAX_DEG,
            ),
          }),
        onPlus: () =>
          this.mutate({
            pitchDeg: clamp(
              s.pitchDeg + SETTINGS_ANGLE_STEP_DEG,
              SETTINGS_ANGLE_MIN_DEG,
              SETTINGS_ANGLE_MAX_DEG,
            ),
          }),
      },
      {
        label: t('settingsYaw')(s.yawDeg),
        onMinus: () =>
          this.mutate({
            yawDeg: clamp(
              s.yawDeg - SETTINGS_ANGLE_STEP_DEG,
              SETTINGS_ANGLE_MIN_DEG,
              SETTINGS_ANGLE_MAX_DEG,
            ),
          }),
        onPlus: () =>
          this.mutate({
            yawDeg: clamp(
              s.yawDeg + SETTINGS_ANGLE_STEP_DEG,
              SETTINGS_ANGLE_MIN_DEG,
              SETTINGS_ANGLE_MAX_DEG,
            ),
          }),
      },
      {
        label: t('settingsRoll')(s.rollDeg),
        onMinus: () =>
          this.mutate({
            rollDeg: clamp(
              s.rollDeg - SETTINGS_ANGLE_STEP_DEG,
              SETTINGS_ANGLE_MIN_DEG,
              SETTINGS_ANGLE_MAX_DEG,
            ),
          }),
        onPlus: () =>
          this.mutate({
            rollDeg: clamp(
              s.rollDeg + SETTINGS_ANGLE_STEP_DEG,
              SETTINGS_ANGLE_MIN_DEG,
              SETTINGS_ANGLE_MAX_DEG,
            ),
          }),
      },
    ];

    // 4 step rows + hand row + turn row = 6 rows.
    const pitch = this.contentPitch(ch, 6);
    const rowH = ch * 0.07;
    let i = 0;
    for (const row of rows) {
      const y = this.contentTop(ch) + i * pitch;
      this.pushStepRow(buttons, row, cw, y, rowH);
      labels.push({ text: row.label, x: cw * 0.06, y: y + rowH * 0.5 });
      i++;
    }

    // Hand row.
    const yHand = this.contentTop(ch) + i * pitch;
    labels.push({ text: `${t('settingsHand')}:`, x: cw * 0.06, y: yHand + rowH * 0.5 });
    buttons.push({
      rect: { x: cw * 0.45, y: yHand, w: cw * 0.22, h: rowH },
      label: t('settingsHandLeft'),
      variant: s.dominantHand === DOMINANT_HAND.left ? 'active' : 'secondary',
      onClick: () => this.mutate({ dominantHand: DOMINANT_HAND.left }),
    });
    buttons.push({
      rect: { x: cw * 0.7, y: yHand, w: cw * 0.22, h: rowH },
      label: t('settingsHandRight'),
      variant: s.dominantHand === DOMINANT_HAND.right ? 'active' : 'secondary',
      onClick: () => this.mutate({ dominantHand: DOMINANT_HAND.right }),
    });
    i++;

    // Turn-mode row (15° / 30° / 45° / Cont.).
    const yTurn = this.contentTop(ch) + i * pitch;
    labels.push({ text: `${t('settingsTurn')}:`, x: cw * 0.06, y: yTurn + rowH * 0.5 });
    const turnOptions: Array<{ mode: TurnMode; label: string }> = [
      { mode: TURN_MODE.snap15, label: '15°' },
      { mode: TURN_MODE.snap30, label: '30°' },
      { mode: TURN_MODE.snap45, label: '45°' },
      { mode: TURN_MODE.continuous, label: 'Cont.' },
    ];
    const turnW = cw * 0.12;
    const turnGap = cw * 0.13;
    const turnX0 = cw * 0.4;
    turnOptions.forEach((opt, j) => {
      buttons.push({
        rect: { x: turnX0 + j * turnGap, y: yTurn, w: turnW, h: rowH },
        label: opt.label,
        variant: s.turnMode === opt.mode ? 'active' : 'secondary',
        onClick: () => this.mutate({ turnMode: opt.mode }),
      });
    });
  }

  // ── Mode tab: pell zone bands, or targets size/speed/distance ──
  private layoutMode(
    buttons: PanelButton[],
    labels: Array<{ text: string; x: number; y: number; muted?: boolean }>,
    cw: number,
    ch: number,
  ): void {
    const s = this.state!;
    if (s.mode === 'guard') {
      this.layoutGuard(buttons, labels, cw, ch);
      return;
    }
    if (s.mode === 'targets') {
      const rows: StepRow[] = [];
      if (s.targetsSizeFactor !== undefined) {
        const v = s.targetsSizeFactor;
        rows.push({
          label: t('settingsTargetSize')(v),
          onMinus: () =>
            this.mutate({
              targetsSizeFactor: clamp(
                round2(v - TARGETS_SIZE_FACTOR_STEP),
                TARGETS_SIZE_FACTOR_MIN,
                TARGETS_SIZE_FACTOR_MAX,
              ),
            }),
          onPlus: () =>
            this.mutate({
              targetsSizeFactor: clamp(
                round2(v + TARGETS_SIZE_FACTOR_STEP),
                TARGETS_SIZE_FACTOR_MIN,
                TARGETS_SIZE_FACTOR_MAX,
              ),
            }),
        });
      }
      if (s.targetsMinSpeed !== undefined) {
        const v = s.targetsMinSpeed;
        rows.push({
          label: t('settingsMinSpeed')(v),
          onMinus: () =>
            this.mutate({
              targetsMinSpeed: clamp(
                round2(v - TARGETS_MIN_SPEED_STEP_MPS),
                TARGETS_MIN_SPEED_MIN_MPS,
                TARGETS_MIN_SPEED_MAX_MPS,
              ),
            }),
          onPlus: () =>
            this.mutate({
              targetsMinSpeed: clamp(
                round2(v + TARGETS_MIN_SPEED_STEP_MPS),
                TARGETS_MIN_SPEED_MIN_MPS,
                TARGETS_MIN_SPEED_MAX_MPS,
              ),
            }),
        });
      }
      if (s.targetsDistanceM !== undefined) {
        const v = s.targetsDistanceM;
        rows.push({
          label: t('settingsDistance')(v),
          onMinus: () =>
            this.mutate({
              targetsDistanceM: clamp(
                round1(v - TARGETS_DISTANCE_STEP_M),
                TARGETS_DISTANCE_MIN_M,
                TARGETS_DISTANCE_MAX_M,
              ),
            }),
          onPlus: () =>
            this.mutate({
              targetsDistanceM: clamp(
                round1(v + TARGETS_DISTANCE_STEP_M),
                TARGETS_DISTANCE_MIN_M,
                TARGETS_DISTANCE_MAX_M,
              ),
            }),
        });
      }
      if (s.targetsTtlMs !== undefined) {
        const v = s.targetsTtlMs;
        rows.push({
          label: t('settingsTargetTtl')(v / 1000),
          onMinus: () =>
            this.mutate({
              targetsTtlMs: clamp(v - TARGETS_TTL_STEP_MS, TARGETS_TTL_MIN_MS, TARGETS_TTL_MAX_MS),
            }),
          onPlus: () =>
            this.mutate({
              targetsTtlMs: clamp(v + TARGETS_TTL_STEP_MS, TARGETS_TTL_MIN_MS, TARGETS_TTL_MAX_MS),
            }),
        });
      }
      if (s.targetsSpawnDelayMs !== undefined) {
        const v = s.targetsSpawnDelayMs;
        rows.push({
          label: t('settingsTargetSpawnDelay')(v / 1000),
          onMinus: () =>
            this.mutate({
              targetsSpawnDelayMs: clamp(
                v - TARGETS_SPAWN_DELAY_STEP_MS,
                TARGETS_SPAWN_DELAY_MIN_MS,
                TARGETS_SPAWN_DELAY_MAX_MS,
              ),
            }),
          onPlus: () =>
            this.mutate({
              targetsSpawnDelayMs: clamp(
                v + TARGETS_SPAWN_DELAY_STEP_MS,
                TARGETS_SPAWN_DELAY_MIN_MS,
                TARGETS_SPAWN_DELAY_MAX_MS,
              ),
            }),
        });
      }
      if (s.targetsTipMinT !== undefined) {
        const v = s.targetsTipMinT;
        rows.push({
          label: t('settingsTargetTipMinT')(Math.round(v * 100)),
          onMinus: () =>
            this.mutate({
              targetsTipMinT: clamp(
                round2(v - TARGETS_TIP_MIN_T_STEP),
                TARGETS_TIP_MIN_T_MIN,
                TARGETS_TIP_MIN_T_MAX,
              ),
            }),
          onPlus: () =>
            this.mutate({
              targetsTipMinT: clamp(
                round2(v + TARGETS_TIP_MIN_T_STEP),
                TARGETS_TIP_MIN_T_MIN,
                TARGETS_TIP_MIN_T_MAX,
              ),
            }),
        });
      }
      const pitch = this.contentPitch(ch, Math.max(rows.length, 4));
      const rowH = ch * 0.07;
      rows.forEach((row, i) => {
        const y = this.contentTop(ch) + i * pitch;
        this.pushStepRow(buttons, row, cw, y, rowH, s.modeReadOnly ?? false);
        labels.push({ text: row.label, x: cw * 0.06, y: y + rowH * 0.5 });
      });
      return;
    }

    if (s.mode === 'arcade') {
      const rows: StepRow[] = [];
      if (s.arcadeTtlMs !== undefined) {
        const v = s.arcadeTtlMs;
        rows.push({
          label: t('settingsArcadeTtl')(v / 1000),
          onMinus: () =>
            this.mutate({
              arcadeTtlMs: clamp(v - ARCADE_TTL_STEP_MS, ARCADE_TTL_MIN_MS, ARCADE_TTL_MAX_MS),
            }),
          onPlus: () =>
            this.mutate({
              arcadeTtlMs: clamp(v + ARCADE_TTL_STEP_MS, ARCADE_TTL_MIN_MS, ARCADE_TTL_MAX_MS),
            }),
        });
      }
      if (s.arcadeSpawnDelayMs !== undefined) {
        const v = s.arcadeSpawnDelayMs;
        rows.push({
          label: t('settingsArcadeSpawnDelay')(v / 1000),
          onMinus: () =>
            this.mutate({
              arcadeSpawnDelayMs: clamp(
                v - ARCADE_SPAWN_DELAY_STEP_MS,
                ARCADE_SPAWN_DELAY_MIN_MS,
                ARCADE_SPAWN_DELAY_MAX_MS,
              ),
            }),
          onPlus: () =>
            this.mutate({
              arcadeSpawnDelayMs: clamp(
                v + ARCADE_SPAWN_DELAY_STEP_MS,
                ARCADE_SPAWN_DELAY_MIN_MS,
                ARCADE_SPAWN_DELAY_MAX_MS,
              ),
            }),
        });
      }
      if (s.arcadeCutToleranceDeg !== undefined) {
        const v = s.arcadeCutToleranceDeg;
        rows.push({
          label: t('settingsArcadeCutTol')(v),
          onMinus: () =>
            this.mutate({
              arcadeCutToleranceDeg: clamp(
                v - ARCADE_CUT_DIR_TOLERANCE_STEP_DEG,
                ARCADE_CUT_DIR_TOLERANCE_MIN_DEG,
                ARCADE_CUT_DIR_TOLERANCE_MAX_DEG,
              ),
            }),
          onPlus: () =>
            this.mutate({
              arcadeCutToleranceDeg: clamp(
                v + ARCADE_CUT_DIR_TOLERANCE_STEP_DEG,
                ARCADE_CUT_DIR_TOLERANCE_MIN_DEG,
                ARCADE_CUT_DIR_TOLERANCE_MAX_DEG,
              ),
            }),
        });
      }
      if (s.arcadeSizeFactor !== undefined) {
        const v = s.arcadeSizeFactor;
        rows.push({
          label: t('settingsArcadeSize')(v),
          onMinus: () =>
            this.mutate({
              arcadeSizeFactor: clamp(
                round2(v - ARCADE_SIZE_FACTOR_STEP),
                ARCADE_SIZE_FACTOR_MIN,
                ARCADE_SIZE_FACTOR_MAX,
              ),
            }),
          onPlus: () =>
            this.mutate({
              arcadeSizeFactor: clamp(
                round2(v + ARCADE_SIZE_FACTOR_STEP),
                ARCADE_SIZE_FACTOR_MIN,
                ARCADE_SIZE_FACTOR_MAX,
              ),
            }),
        });
      }
      if (s.arcadeMinSpeed !== undefined) {
        const v = s.arcadeMinSpeed;
        rows.push({
          label: t('settingsArcadeMinSpeed')(v),
          onMinus: () =>
            this.mutate({
              arcadeMinSpeed: clamp(
                round1(v - ARCADE_MIN_SPEED_STEP_MPS),
                ARCADE_MIN_SPEED_MIN_MPS,
                ARCADE_MIN_SPEED_MAX_MPS,
              ),
            }),
          onPlus: () =>
            this.mutate({
              arcadeMinSpeed: clamp(
                round1(v + ARCADE_MIN_SPEED_STEP_MPS),
                ARCADE_MIN_SPEED_MIN_MPS,
                ARCADE_MIN_SPEED_MAX_MPS,
              ),
            }),
        });
      }
      const pitch = this.contentPitch(ch, Math.max(rows.length, 4));
      const rowH = ch * 0.07;
      rows.forEach((row, i) => {
        const y = this.contentTop(ch) + i * pitch;
        this.pushStepRow(buttons, row, cw, y, rowH, s.modeReadOnly ?? false);
        labels.push({ text: row.label, x: cw * 0.06, y: y + rowH * 0.5 });
      });
      return;
    }

    // Pell mode: strike-validity selectors (min speed + min arc), then the 4 zone-boundary rows.
    const rowH = ch * 0.07;
    const selectorRows: StepRow[] = [];
    if (s.pellMinSpeed !== undefined) {
      const v = s.pellMinSpeed;
      selectorRows.push({
        label: t('settingsPellMinSpeed')(v),
        onMinus: () =>
          this.mutate({
            pellMinSpeed: clamp(
              round1(v - PELL_STRIKE_MIN_SPEED_STEP_MPS),
              PELL_STRIKE_MIN_SPEED_MIN_MPS,
              PELL_STRIKE_MIN_SPEED_MAX_MPS,
            ),
          }),
        onPlus: () =>
          this.mutate({
            pellMinSpeed: clamp(
              round1(v + PELL_STRIKE_MIN_SPEED_STEP_MPS),
              PELL_STRIKE_MIN_SPEED_MIN_MPS,
              PELL_STRIKE_MIN_SPEED_MAX_MPS,
            ),
          }),
      });
    }
    if (s.pellMinArc !== undefined) {
      const v = s.pellMinArc;
      selectorRows.push({
        label: t('settingsPellMinArc')(v),
        onMinus: () =>
          this.mutate({
            pellMinArc: clamp(
              round1(v - PELL_STRIKE_MIN_ARC_STEP_M),
              PELL_STRIKE_MIN_ARC_MIN_M,
              PELL_STRIKE_MIN_ARC_MAX_M,
            ),
          }),
        onPlus: () =>
          this.mutate({
            pellMinArc: clamp(
              round1(v + PELL_STRIKE_MIN_ARC_STEP_M),
              PELL_STRIKE_MIN_ARC_MIN_M,
              PELL_STRIKE_MIN_ARC_MAX_M,
            ),
          }),
      });
    }

    const boundaries = s.zoneBoundaries ?? [];
    const zoneNames = [
      t('zoneLeg'),
      t('zoneFlank'),
      t('zoneChest'),
      t('zoneShoulder'),
      t('zoneHead'),
    ];
    // selector rows + a title row + one row per boundary.
    const pitch = this.contentPitch(ch, selectorRows.length + 1 + boundaries.length);
    let i = 0;

    for (const row of selectorRows) {
      const y = this.contentTop(ch) + i * pitch;
      this.pushStepRow(buttons, row, cw, y, rowH);
      labels.push({ text: row.label, x: cw * 0.06, y: y + rowH * 0.5 });
      i++;
    }

    labels.push({
      text: t('settingsZoneBandsTitle'),
      x: cw * 0.06,
      y: this.contentTop(ch) + i * pitch + rowH * 0.5,
      muted: true,
    });
    i++;

    boundaries.forEach((value, bi) => {
      const y = this.contentTop(ch) + i * pitch;
      const pct = Math.round(value * 100);
      const label = t('settingsZoneBand')(zoneNames[bi] ?? '', zoneNames[bi + 1] ?? '', pct);
      this.pushStepRow(
        buttons,
        {
          label,
          onMinus: () => this.mutateBoundary(bi, -PELL_ZONE_BAND_STEP),
          onPlus: () => this.mutateBoundary(bi, +PELL_ZONE_BAND_STEP),
        },
        cw,
        y,
        rowH,
      );
      labels.push({ text: label, x: cw * 0.06, y: y + rowH * 0.5 });
      i++;
    });
  }

  // ── Guard tab: the 8 live recognition tunables (VG2). ──
  private layoutGuard(
    buttons: PanelButton[],
    labels: Array<{ text: string; x: number; y: number; muted?: boolean }>,
    cw: number,
    ch: number,
  ): void {
    const g = this.state!.guardTuning;
    if (!g) return;
    const guardRow = (key: keyof GuardTuning, label: string): StepRow => {
      const r = GUARD_TUNING_RANGES[key];
      const cur = g[key];
      const set = (nv: number): void =>
        this.mutate({ guardTuning: { ...g, [key]: clamp(round2(nv), r.min, r.max) } });
      return { label, onMinus: () => set(cur - r.step), onPlus: () => set(cur + r.step) };
    };
    const rows: StepRow[] = [
      guardRow('stillHandEnterMps', t('guardSetStillHand')(g.stillHandEnterMps)),
      guardRow('stillTipEnterMps', t('guardSetStillTip')(g.stillTipEnterMps)),
      guardRow('dwellMs', t('guardSetDwell')(g.dwellMs)),
      guardRow('handAltaMinFrac', t('guardSetHandAlta')(Math.round(g.handAltaMinFrac * 100))),
      guardRow('aimVertBandM', t('guardSetAimVert')(Math.round(g.aimVertBandM * 100))),
      guardRow('aimCentroM', t('guardSetAimCentro')(Math.round(g.aimCentroM * 100))),
      guardRow('aimSideOnFigM', t('guardSetAimOnFig')(Math.round(g.aimSideOnFigM * 100))),
      guardRow('handRollDownMin', t('guardSetRollDown')(Math.round(g.handRollDownMin * 100))),
      guardRow('handRollFlatMax', t('guardSetRollFlat')(Math.round(g.handRollFlatMax * 100))),
    ];
    const pitch = this.contentPitch(ch, rows.length);
    const rowH = ch * 0.07;
    rows.forEach((row, i) => {
      const y = this.contentTop(ch) + i * pitch;
      this.pushStepRow(buttons, row, cw, y, rowH);
      labels.push({ text: row.label, x: cw * 0.06, y: y + rowH * 0.5 });
    });
  }

  // ── Audio tab: master SFX + music volumes (0..1, shown as %). ──
  private layoutAudio(
    buttons: PanelButton[],
    labels: Array<{ text: string; x: number; y: number; muted?: boolean }>,
    cw: number,
    ch: number,
  ): void {
    const s = this.state!;
    const volRow = (
      get: () => number,
      set: (v: number) => void,
      label: (pct: number) => string,
    ): StepRow => {
      const cur = get();
      const apply = (nv: number): void =>
        set(clamp(round2(nv), AUDIO_VOLUME_MIN, AUDIO_VOLUME_MAX));
      return {
        label: label(Math.round(cur * 100)),
        onMinus: () => apply(cur - AUDIO_VOLUME_STEP),
        onPlus: () => apply(cur + AUDIO_VOLUME_STEP),
      };
    };
    const rows: StepRow[] = [
      volRow(
        () => s.sfxVolume,
        (v) => this.mutate({ sfxVolume: v }),
        t('settingsSfxVolume'),
      ),
      volRow(
        () => s.musicVolume,
        (v) => this.mutate({ musicVolume: v }),
        t('settingsMusicVolume'),
      ),
    ];
    const pitch = this.contentPitch(ch, Math.max(rows.length, 4));
    const rowH = ch * 0.07;
    rows.forEach((row, i) => {
      const y = this.contentTop(ch) + i * pitch;
      this.pushStepRow(buttons, row, cw, y, rowH);
      labels.push({ text: row.label, x: cw * 0.06, y: y + rowH * 0.5 });
    });
  }

  /** Pinned bottom row, always present: Ripeti · Cambia sessione · Annulla sessione · Chiudi. */
  private layoutActions(buttons: PanelButton[], cw: number, ch: number): void {
    const bottomY = ch * 0.88;
    const bottomH = ch * 0.09;
    const actions: Array<{ label: string; variant: 'primary' | 'secondary'; onClick: () => void }> =
      [
        {
          label: t('summaryRestart'),
          variant: 'secondary',
          onClick: () => this.handlers?.onRestart(),
        },
        {
          label: t('settingsCancelSession'),
          variant: 'secondary',
          onClick: () => this.handlers?.onCancelSession(),
        },
        { label: t('settingsClose'), variant: 'primary', onClick: () => this.handlers?.onClose() },
      ];
    const btnW = cw * 0.22;
    const gap = cw * 0.02;
    const totalW = btnW * actions.length + gap * (actions.length - 1);
    let x = (cw - totalW) / 2;
    for (const a of actions) {
      buttons.push({
        rect: { x, y: bottomY, w: btnW, h: bottomH },
        label: a.label,
        variant: a.variant,
        onClick: a.onClick,
      });
      x += btnW + gap;
    }
  }

  /** Top Y (px) of the tab content area, below the tab row. */
  private contentTop(ch: number): number {
    return ch * 0.3;
  }

  /** Vertical spacing (px) between content rows so `nRows` fit above the action row. */
  private contentPitch(ch: number, nRows: number): number {
    const available = ch * 0.84 - this.contentTop(ch);
    return available / Math.max(1, nRows);
  }

  private pushStepRow(
    buttons: PanelButton[],
    row: StepRow,
    cw: number,
    y: number,
    rowH: number,
    readOnly = false,
  ): void {
    // Read-only (arcade preset): show the value label only — the ± controls are omitted so the
    // bundle-owned tunables can't be edited (#43).
    if (readOnly) return;
    const btnSize = rowH;
    buttons.push({
      rect: { x: cw * 0.66, y, w: btnSize, h: rowH },
      label: '−',
      variant: 'secondary',
      onClick: row.onMinus,
    });
    buttons.push({
      rect: { x: cw * 0.83, y, w: btnSize, h: rowH },
      label: '+',
      variant: 'secondary',
      onClick: row.onPlus,
    });
  }

  private mutate(patch: Partial<SettingsState>): void {
    if (!this.state || !this.handlers) return;
    this.state = { ...this.state, ...patch };
    this.handlers.onChange(this.state);
    this.repaint();
  }

  /** Adjust the i-th zone boundary by `delta`, clamped between its neighbors with a min gap. */
  private mutateBoundary(i: number, delta: number): void {
    const s = this.state;
    if (!s?.zoneBoundaries) return;
    const arr = s.zoneBoundaries.slice();
    const gap = PELL_ZONE_BAND_MIN_GAP;
    const lo = (arr[i - 1] ?? 0) + gap;
    const hi = (arr[i + 1] ?? 1) - gap;
    arr[i] = clamp(round2((arr[i] ?? 0) + delta), lo, hi);
    this.mutate({ zoneBoundaries: arr });
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}
